<template>
  <b-modal
    id="mc-modal-renewal-contract"
    ref="mc-modal-renewal-contract"
    class="mc-modal"
    title="Konfirmasi Pembaruan Kontrak"
    size="md"
    centered
    scrollable
    no-close-on-backdrop
    @close="handleClose"
    @hidden="handleHide"
  >
    <div class="card-body">
      <McAlert withIcon class="w-100 mb-3" variant="warning">
        <template #label>
          <span><b>PERHATIAN!</b> Kontrak saat ini akan <b>ditutup</b> setelah aksi pembaruan berhasil.</span>
        </template>
      </McAlert>
      <p class="text-grey">Kontrak baru akan dibuat berdasarkan data kontrak saat ini.</p>
      <p v-if="!isBusy && totalCount > 0" class="text-grey">Pilih kendaraan yang akan dimasukkan ke kontrak baru (opsional):</p>
      <div class="p-3 vehicle-list-container">
        <template v-if="totalCount === 0 && search === '' && ids.length === 0">
          <div class="d-flex gap-3">
            <img :src="require('@/assets/images/contract/no-truck-available.svg')" width="56px" />
            <div>
              <h6>Belum ada kendaraan pada kontrak</h6>
              <span class="text-muted">Anda tetap dapat melanjutkan pembaruan kontrak tanpa memilih kendaraan.</span>
            </div>
          </div>
        </template>
        <template v-else>
          <b-row align-h="between" class="mb-3">
            <b-col md="8" xl="8">
              <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
                <span class="badge btn-success">{{ selectedIdsLength }}</span>
                Baris terpilih.
                <a
                  class="color-primary"
                  @click="handleUnselectAll"
                >
                  Bersihkan pilihan
                </a>
              </h6>
            </b-col>
            <b-col md="4" xl="4">
              <b-input-group
                size="sm"
                class="mc-has-search"
              >
                <template #prepend>
                  <i class="ps-2 mc-icon icon-fill_search" />
                </template>
                <b-form-input
                  ref="searchInput"
                  placeholder="Cari..."
                  :disabled="disabled"
                  :value="search"
                  @input="handleSearchChange"
                />
                <template
                  v-if="search"
                  #append
                >
                  <button
                    class="clear-search-btn"
                    :disabled="disabled"
                    @click="handleClearSearch"
                  >
                    <i class="pe-2 mc-icon icon-outline_close"></i>
                  </button>
                </template>
              </b-input-group>
            </b-col>
          </b-row>
          <div class="table-responsive border border-1">
            <b-table
              :busy="isBusy"
              :items="items"
              :fields="computedFields"
              show-empty
            >              
              <template #empty>
                <div v-show="!isBusy" class="text-center my-4">
                  Tidak ada data
                </div>
              </template>

              <!-- Slot: Header Checkbox -->
              <template #head(checkbox)>
                <div class="form-check" style="width: 10px">
                  <b-form-checkbox
                    v-model="selectedAll"
                    :disabled="disabled || items.length === 0"
                    @change="handleSelectAll"
                  />
                </div>
              </template>

              <!-- Slot: Cell Checkbox -->
              <template #cell(checkbox)="data">
                <div class="form-check">
                  <b-form-checkbox
                    :checked="selectedIds[data.item.id]"
                    :disabled="disabled"
                    @change="(event) => handleSelect(event, data)"
                  />
                </div>
              </template>

              <!-- Slot: All Cells -->
              <template #cell()="{ field, value }">
                <div>{{ formatValue(value, field) }}</div>
              </template>
            </b-table>

            <!-- Observe visibility to get next page data -->
            <div v-observe-visibility="handleElementObserved" style="height: 1px"></div>

            <!-- Skeleton when getting the data -->
            <div v-if="isBusy && hasNextPage" class="px-2 py-2 d-flex gap-2">
              <b-skeleton width="2.5%" />
              <b-skeleton width="97.5%" />
            </div>  
          </div>
        </template>
      </div>
    </div>
    <template #modal-footer="{ cancel }">
      <div class="d-flex">
        <McButton
          variant="secondary"
          class="me-2"
          :text="$t('global.cancel')"
          :disabled="isBusy"
          @click="cancel()"
        />
        <McButton
          variant="primary"
          text="Perbarui Kontrak"
          :loading="isBusy"
          @click="handleRenewal"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import { debounce } from '@/helpers/debounce'

import McAlert from '@/components/McAlert'
import McButton from '@/components/McButton'

import fields from '../lib/fields/renewal-contract-fields'

import * as CustomerContract from '@/services/rest-api/lib/contract/customer-contract'

export default {
  components: {
    McAlert,
    McButton,
  },
  mixins: [fields],
  data() {
    return {
      isBusy: false,

      page: 1,
      limit: 10,
      totalCount: 0,

      search: '',
      items: [],
      ids: [],

      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
    }
  },
  computed: {
    disabled() {
      return this.isBusy
    },
    computedFields() {
      let fields = [
        {
          key: 'checkbox',
          label: '',
          thStyle: 'width: 25px',
        },
        ...this.fields,
      ]

      return fields
    },
    hasNextPage() {
      if (!this.totalCount) return true
      return this.page * this.limit < this.totalCount
    },
    // Customize Api CLient
    apiClient() {
      return {
        get: async (filter) => {
          let result = {
            metadata: {
              count: 0,
              page: 1,
              total_count: 0,
              total_page: 1,
            },
            data: {
              paginated_result: [],
            },
          }

          try {
            const res = await CustomerContract.getListVehicle(this.contractId, filter)

            if (res.errors) {
              this.$mcToast.error('Terjadi kesalahan.')
            } else {
              result = res
            }
          } catch(e) {
            console.log(e)
          }
          return result
        },
      }
    },
    contractId() {
      return this.$route.params.contractId
    },
  },
  methods: {
    async show() {
      this.$refs['mc-modal-renewal-contract'].show()
      await this.getData({
        limit: this.limit,
        page: this.page,
      })
    },

    hide() {
      this.$refs['mc-modal-renewal-contract'].hide()
    },

    async getData(params) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      this.isBusy = true
      try {
        const res = await this.apiClient.get(params)

        if (!res || res?.errors) {
          this.isBusy = false
          return
        }

        const newItems = res.data.paginated_result || []
        this.items = [...this.items, ...newItems]

        this.ids = res.data.ids

        this.page = res.metadata.page
        this.totalCount = res.metadata.total_count

        if (this.totalCount === 0) {
          this.items = []
          this.page = 1
        }

        await this.$nextTick()
      } catch (e) {
        this.$mcToast.error('Terjadi kesalahan')

        // reset data
        this.items = []
        this.ids = []
        this.page = 1
        this.totalCount = 0
      } finally {
        this.isBusy = false
      }
    },

    handleSearchChange: debounce(async function (_search) {
      this.items = []
      this.page = 1
      this.search = _search
      const opts = {
        limit: this.limit,
        page: 1,
        search: _search != '' ? _search : undefined,
      }
      await this.getData(opts)
    }, 500),

    handleClearSearch() {
      this.search = ''
      this.handleSearchChange(undefined)
    },
    
    formatValue(value, field) {
      if (field.formatValue === undefined) {
        return value
      }

      return field.formatValue(value)
    },

    
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },

    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },

    handleUnselectAll() {
      if (this.isBusy) return

      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },

    handleElementObserved(isVisible) {
      if (!isVisible || this.isBusy) return
      if (!this.hasNextPage) return
      // if there is no items and has the search query
      // then we don't have to get data by observer
      if (this.items.length == 0 && this.search) return

      // if total count is null, it means that we haven't fetch the data (initial)
      // do not increment page, but still fetch the data
      this.getData({
        limit: this.limit,
        page: this.totalCount ? this.page + 1 : this.page,
        search: this.search != '' ? this.search : undefined,
      })
    },

    handleClose(bvModalEvt) {
      if (this.isBusy) {
        bvModalEvt.preventDefault();
      }
    },

    handleHide() {
      this.handleUnselectAll()

      this.items = []
      this.ids = []
      this.page = 1
      this.search = ''
    },

    async handleRenewal() {
      try {
        this.isBusy = true
        const selectedVehicleIds = []
        for (let key in this.selectedIds) {
          if (this.selectedIds[key]) {
            selectedVehicleIds.push(key)
          }
        }
  
        const formattedData = {
          customer_contract_vehicle_ids: selectedVehicleIds,
        }
  
        const renewalData = await CustomerContract.renewal(this.contractId, formattedData)
  
        if (!renewalData?.data?.id || renewalData?.errors) {
          if (renewalData?.errors_v2?.issues) {
            renewalData.errors_v2.issues.map(issue => {
              if (issue.code === 'ORDER.CUSTOMER.CONTRACT_ALREADY_RENEWED') {
                const contractNumber = issue.message.split(' ').slice(-1)[0]
                
                this.$mcToast.error({
                  component: 'McToastDynamicBody',
                  props: {
                    contents: [
                      {
                        tag: 'span',
                        text: 'Kontrak Pelanggan sudah diperbarui dengan nomor kontrak',
                      },
                      {
                        tag: 'strong',
                        text: contractNumber,
                      },
                    ],
                  },
                })
              } else {
                this.$mcToast.error(issue.message)
              }
            })
          } else {
            this.$mcToast.error('Kontrak pelanggan gagal diperbarui')
          }

          return
        }
  
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                tag: 'span',
                text: 'Kontrak',
              },
              {
                tag: 'strong',
                text: renewalData.data.number,
              },
              {
                tag: 'span',
                text: 'berhasil dibuat dan disimpan sebagai',
              },
              {
                tag: 'strong',
                text: 'Draf',
              },
            ],
          },
        })
  
        this.hide()
  
        this.$router.push({
          name: 'Master Customer Contract Detail',
          params: {
            contractId: renewalData.data.id,
          },
        })
      } catch (e) {
        this.$mcToast.error('Kontrak pelanggan gagal diperbarui')
      } finally {
        this.isBusy = false
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.table-responsive {
  overflow-y: auto;
  max-height: 200px;
}

::v-deep .table-responsive > .table.b-table > thead > tr > th {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  z-index: 2;
} 

.mc-has-search {
  border-width: $input-border-width;
  border-color: $input-border-color;
  border-style: solid;
  border-radius: 4px;

  // if search is disabled
  &:has(input:disabled) {
    background: $input-disabled-bg;
  }

  .clear-search-btn {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .clear-search-btn:disabled {
    cursor: default;
  }

  input {
    border: none;
    border-radius: 4px;
  }

  .input-group-prepend,
  .input-group-append {
    border: none;
    display: flex;
    align-items: center;

    .mc-icon:before {
      font-size: 16px;
      color: $input-placeholder-color;
    }
  }
}

.text-grey {
  color: $mep-color-text-400;
}

.vehicle-list-container {
  border: 1px solid #E6E7EC;
  border-radius: 4px;
}
</style>